import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Form,
  Input,
  Radio,
  Space,
  Switch,
  Upload,
} from 'antd';
import {
  DeleteOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { defaultBackground, defaultOverlay } from '../constants';
import BackgroundCutoffPreview from './BackgroundCutoffPreview';

const { TextArea } = Input;

function ImageField({
  label,
  url,
  onChange,
  onClear,
  clearLabel,
  accept = 'image/*',
  showThumb = true,
  extra,
}) {
  const inputKeyRef = useRef(0);

  return (
    <Form.Item label={label}>
      <Space direction="vertical" style={{ width: '100%' }}>
        {showThumb && url && (
          <div className="landing-page-simulator__thumb">
            <img src={url} alt="" />
          </div>
        )}
        {extra}
        <Space wrap>
          <Upload
            key={inputKeyRef.current}
            accept={accept}
            showUploadList={false}
            beforeUpload={(file) => {
              const objectUrl = URL.createObjectURL(file);
              onChange(objectUrl, file);
              inputKeyRef.current += 1;
              return false;
            }}
          >
            <Button icon={<UploadOutlined />}>Upload</Button>
          </Upload>
          {onClear && (
            <Button icon={<DeleteOutlined />} onClick={onClear}>
              {clearLabel || 'Clear'}
            </Button>
          )}
        </Space>
      </Space>
    </Form.Item>
  );
}

ImageField.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  clearLabel: PropTypes.string,
  accept: PropTypes.string,
  showThumb: PropTypes.bool,
  extra: PropTypes.node,
};

ImageField.defaultProps = {
  url: undefined,
  onClear: undefined,
  clearLabel: 'Clear',
  accept: 'image/*',
  showThumb: true,
  extra: undefined,
};

function LandingControls({
  config,
  onChange,
  isBackgroundDefault,
  isOverlayDefault,
}) {
  const update = (patch) => onChange({ ...config, ...patch });

  const updateButton = (key, patch) => {
    update({
      [key]: {
        ...config[key],
        ...patch,
      },
    });
  };

  return (
    <div className="landing-page-simulator__controls">
      <Form layout="vertical" size="middle">
        <Form.Item label="Overlay type">
          <Radio.Group
            value={config.overlay}
            onChange={(event) => update({ overlay: event.target.value })}
            optionType="button"
            buttonStyle="solid"
            options={[
              { label: 'Text', value: 'text' },
              { label: 'Image', value: 'image' },
            ]}
          />
        </Form.Item>

        {config.overlay === 'text' && (
          <Form.Item label="Overlay text" extra="Use a new line for each line of copy.">
            <TextArea
              rows={3}
              value={config.text}
              onChange={(event) => update({ text: event.target.value })}
              placeholder="try something new"
            />
          </Form.Item>
        )}

        {config.overlay === 'image' && (
          <ImageField
            label="Overlay image"
            url={config.overlayImageUrl}
            onChange={(url) => {
              if (config.overlayImageUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(config.overlayImageUrl);
              }
              update({ overlayImageUrl: url });
            }}
            onClear={!isOverlayDefault ? () => {
              if (config.overlayImageUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(config.overlayImageUrl);
              }
              update({ overlayImageUrl: defaultOverlay });
            } : undefined}
            clearLabel="Restore default"
          />
        )}

        <ImageField
          label="Background image"
          url={config.backgroundImageUrl}
          showThumb={false}
          extra={(
            <BackgroundCutoffPreview
              imageUrl={config.backgroundImageUrl}
              showAnnouncementBanner={config.showAnnouncementBanner}
            />
          )}
          onChange={(url) => {
            if (config.backgroundImageUrl?.startsWith('blob:')) {
              URL.revokeObjectURL(config.backgroundImageUrl);
            }
            update({ backgroundImageUrl: url });
          }}
          onClear={!isBackgroundDefault ? () => {
            if (config.backgroundImageUrl?.startsWith('blob:')) {
              URL.revokeObjectURL(config.backgroundImageUrl);
            }
            update({ backgroundImageUrl: defaultBackground });
          } : undefined}
          clearLabel="Restore default"
        />

        <Form.Item
          label="Announcement banner"
        >
          <Space>
            <Switch
              checked={config.showAnnouncementBanner}
              onChange={(checked) => update({ showAnnouncementBanner: checked })}
            />
            <span>{config.showAnnouncementBanner ? 'On' : 'Off'}</span>
          </Space>
        </Form.Item>

        <Form.Item label="Logo colour">
          <Space>
            <input
              type="color"
              value={config.logoColor}
              onChange={(event) => update({ logoColor: event.target.value })}
              className="landing-page-simulator__color"
            />
            <Input
              value={config.logoColor}
              onChange={(event) => update({ logoColor: event.target.value })}
              style={{ width: 120 }}
            />
          </Space>
        </Form.Item>

        <Form.Item label="Primary Button">
          <Input
            value={config.primaryButton.label}
            onChange={(event) => updateButton('primaryButton', { label: event.target.value })}
          />
        </Form.Item>

        <Form.Item label="Secondary Button">
          <Input
            value={config.secondaryButton.label}
            onChange={(event) => updateButton('secondaryButton', { label: event.target.value })}
          />
        </Form.Item>
      </Form>
    </div>
  );
}

const configShape = PropTypes.shape({
  backgroundImageUrl: PropTypes.string,
  overlay: PropTypes.oneOf(['text', 'image']).isRequired,
  text: PropTypes.string,
  overlayImageUrl: PropTypes.string,
  logoColor: PropTypes.string.isRequired,
  showAnnouncementBanner: PropTypes.bool,
  primaryButton: PropTypes.shape({
    enabled: PropTypes.bool,
    label: PropTypes.string,
  }).isRequired,
  secondaryButton: PropTypes.shape({
    enabled: PropTypes.bool,
    label: PropTypes.string,
  }).isRequired,
});

LandingControls.propTypes = {
  config: configShape.isRequired,
  onChange: PropTypes.func.isRequired,
  isBackgroundDefault: PropTypes.bool.isRequired,
  isOverlayDefault: PropTypes.bool.isRequired,
};

export default LandingControls;
